import { TENANT_IDS } from "../constants/tenantIds";

type FixedTenant = { id: string; name: string };

export type WastePipelineFileSlot = {
  /** Unique key used to map files end-to-end (registry → S3 → local path → pipeline run) */
  key: string;
  /** Display label shown on the UI upload button */
  label: string;
  /** HTML accept attribute for the file input (e.g. ".xlsx,.xls,.csv") */
  accept: string;
  /** Whether this file must be provided to dispatch the pipeline */
  required: boolean;
};

export type WastePipelineConfig = {
  /** Display label for UI dropdown */
  label: string;
  /** File upload slots — each slot becomes a separate upload in the UI */
  files: WastePipelineFileSlot[];
  /** If set, pipeline is locked to this tenant (skips org picker in UI) */
  fixedTenant: FixedTenant | null;
  /**
   * Optional set of slot keys where the run must supply at least ONE of
   * them (independent of each slot's individual `required` flag). Used
   * when none of the slots are strictly required on their own but the
   * pipeline still needs at least one file. UI and resolver both enforce
   * this.
   */
  requireAtLeastOneOf?: readonly string[];
  /**
   * Tenant IDs that have pipeline-specific custom rules baked into the
   * transforms (e.g. an anomaly check that only fires for one tenant, or a
   * tenant-pending site that's rejected only when that tenant runs it).
   * The dispatch UI uses this to show a "Custom rules apply for this
   * tenant" indicator when the active org is in the list, so staff know to
   * expect divergent behavior vs the vendor-generic baseline.
   *
   * Kept intentionally minimal — just a flag. Detail on *which* rules apply
   * lives in the pipeline's spec/README, not here.
   */
  tenantsWithCustomRules?: readonly string[];
};

export const WASTE_PIPELINE_REGISTRY = {
  "amazon-clean-earth": {
    label: "Amazon Clean Earth",
    // Reportability: classification is never stamped and reportable is blank
    // (engine-derived) except the three determinations the engine can't make —
    // Universal Waste its resolver can't categorize, pharma profiles outside
    // MN, and the TX Class 1 tier (pending STEERS generator-type coverage).
    files: [
      {
        key: "source",
        label: "CE Monthly Reporting Package",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.AMAZON, name: "Amazon" },
  },
  "amazon-clean-harbors": {
    label: "Amazon Clean Harbors",
    // Reportability: same posture as amazon-clean-earth — no classification
    // stamp; reportable blank except unresolvable Universal Waste, pharma
    // outside MN, and the TX Class 1 tier.
    files: [
      { key: "biennial", label: "Biennial Data", accept: ".xlsx,.xls", required: true },
      { key: "monthly", label: "Monthly Data (Optional)", accept: ".xlsx,.xls", required: false },
    ],
    fixedTenant: { id: TENANT_IDS.AMAZON, name: "Amazon" },
  },
  "aspirus-clean-earth": {
    label: "Aspirus Clean Earth",
    // Reportability: only PHRM rows resolved through the Subpart P per-EPA-ID
    // table are stamped — a determination the Compliance Engine has no data
    // for. Every other row (Universal, NHNI, coded) is left blank and the
    // engine derives it at manifest time.
    files: [
      {
        key: "source",
        label: "Clean Earth Monthly Workbook",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.ASPIRUS, name: "Aspirus" },
  },
  "aspirus-stericycle": {
    label: "Aspirus Stericycle",
    // Reportability: WI rows keep their Infectious Waste "Reportable" stamp —
    // a tenant-feed assumption the Compliance Engine deliberately carries no
    // tag rule for. MI rows are blank; the engine derives them.
    files: [
      {
        key: "source",
        label: "Stericycle Monthly Workbook",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.ASPIRUS, name: "Aspirus" },
  },
  "phoenix-childrens-stericycle": {
    label: "Phoenix Children's Stericycle",
    files: [
      {
        key: "source",
        label: "Stericycle Monthly Workbook",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: {
      id: TENANT_IDS.PHOENIX_CHILDREN_S_HOSPITAL,
      name: "Phoenix Children's Hospital",
    },
  },
  "safety-kleen": {
    label: "Safety-Kleen",
    files: [
      {
        key: "source",
        label: "SK Waste Tracking Summary (.xls)",
        accept: ".xls,.xlsx",
        required: true,
      },
    ],
    fixedTenant: null,
    // Reportability is left to the Compliance Engine for every tenant but
    // UPS, which is the only one whose Reportable column is exported.
    //
    // Tenants with tenant-specific Safety-Kleen rules:
    //   - UPS: TX Class 1 non-industrial anomaly (Section 3.5), the
    //     pending KYR000047704 Superfund-site rejection (Appendix A.1), and
    //     the exported reportability determination noted above.
    //   - AEG: per-state tagging rules (LIB in MI, Drained Used Oil
    //     Filters / Oily Wastes / Used Oil in FL) per ENG-15965; rules
    //     live under `apps/lambdas/data-pipeline/src/tenants/aeg/waste/safety-kleen/`.
    //   - Tesla: density from lbs/gal on every row, DOT hazard indicator
    //     mapped from the source, single-column vendor descriptions,
    //     Universal auto-tagging, and classification left to Encamp's
    //     post-ingestion ProcessManifest job.
    // The dispatch UI surfaces a "Custom rules apply" indicator when one
    // of these tenants is selected so reviewers expect divergent behavior.
    tenantsWithCustomRules: [TENANT_IDS.UPS, TENANT_IDS.ATLA_EQUIPMENT_GROUP, TENANT_IDS.TESLA],
  },
  "knauf-waste-management": {
    label: "Knauf Waste Management",
    // The WM workbook is the transactional source; the Profile List is the
    // authoritative Path-1 lookup (description/TSDF/method/tags) and MUST be
    // re-uploaded each run so it stays current (SME decision B1).
    files: [
      {
        key: "source",
        label: "WM Monthly Workbook",
        accept: ".xlsx,.xls",
        required: true,
      },
      {
        key: "profileList",
        label: "Knauf WM Profile List",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.KNAUF, name: "Knauf Insulation" },
  },
  "knauf-arcwood": {
    label: "Knauf Arcwood",
    // Reportability: never stamped — the output column is blank by design and
    // the Compliance Engine derives it; the pipeline's qaReportable signal is
    // QA-workbook-only.
    //
    // The Arcwood portal's Shipping History download is an HTML table saved
    // with an .xls extension — upload the raw portal file as-is (Excel's
    // "file seems corrupt" warning on manual open is expected).
    files: [
      {
        key: "source",
        label: "Arcwood Shipping History (WS_SHIP_RPT.xls)",
        accept: ".xls,.html",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.KNAUF, name: "Knauf Insulation" },
  },
  "knauf-veolia": {
    label: "Knauf Veolia",
    // Reportability is left blank on every line; the Compliance Engine derives
    // it at manifest time. The pipeline's former code-based stamp was retired
    // once the engine's non-haz baseline made a blank mean "engine decides"
    // rather than collapsing to reportable.
    //
    // Veolia's portal exports Knauf's month as two separate reports (haz +
    // non-haz) with the same `WasteGenMgtFlat` schema; the loader concatenates
    // them before any dedup so a manifest whose lines split across the two
    // files is handled as one manifest. Neither slot is required on its own —
    // January 2026's non-haz export had zero data rows because all that
    // month's activity was hazardous — so `requireAtLeastOneOf` enforces that
    // a run still supplies at least one.
    files: [
      {
        key: "hazardous",
        label: "Haz Waste Management Report",
        accept: ".xlsx,.xls",
        required: false,
      },
      {
        key: "nonHazardous",
        label: "Non-Haz Waste Management Report",
        accept: ".xlsx,.xls",
        required: false,
      },
    ],
    requireAtLeastOneOf: ["hazardous", "nonHazardous"],
    fixedTenant: { id: TENANT_IDS.KNAUF, name: "Knauf Insulation" },
  },
  "clean-harbors": {
    label: "Clean Harbors",
    // Reportability: classification is never stamped and reportable is blank
    // (engine-derived) except Universal Waste the engine's resolver can't
    // categorize. No TX Class 1 stamp — that assumption is Amazon-specific;
    // the engine tiers Class 1 from the facility's STEERS generator type.
    // global-partners-clean-harbors reuses these transforms unchanged.
    files: [
      {
        key: "wasteTracking",
        label: "Waste Tracking Data CSV (monthly)",
        accept: ".csv",
        required: false,
      },
      { key: "biennial", label: "Biennial Data CSV (monthly)", accept: ".csv", required: false },
      {
        key: "weeklyXlsx",
        label: "Weekly Tracking File (xlsx/xlsm)",
        accept: ".xlsx,.xlsm",
        required: false,
      },
    ],
    fixedTenant: null,
    // Global Partners has its own Clean Harbors variant pipeline
    // (global-partners-clean-harbors) with extra tagging rules. Dispatching
    // the vendor-generic pipeline for it works but silently skips those
    // rules — the indicator steers staff to the tenant-specific entry.
    // (Petro Star's variant is auto-routed via WASTE_PIPELINE_TENANT_VARIANTS
    // instead, so no indicator is needed for it here.)
    tenantsWithCustomRules: [TENANT_IDS.GLOBAL_PARTNERS],
  },
  "global-partners-clean-harbors": {
    label: "Global Partners Clean Harbors",
    // Copy of the Clean Harbors monthly pipeline for Global Partners (see the
    // pipeline's PIPELINE_SPEC.md addendum). Monthly CSVs only — Global has no
    // weekly feed, so both slots are strictly required.
    // Reportability: inherits the base clean-harbors posture (blank except
    // unresolvable Universal Waste).
    files: [
      {
        key: "wasteTracking",
        label: "Waste Tracking Data CSV (monthly)",
        accept: ".csv",
        required: true,
      },
      { key: "biennial", label: "Biennial Data CSV (monthly)", accept: ".csv", required: true },
    ],
    fixedTenant: { id: TENANT_IDS.GLOBAL_PARTNERS, name: "Global Partners" },
  },
  "petro-star-clean-harbors": {
    label: "Petro Star Clean Harbors",
    // Monthly CSVs only — Petro Star has no weekly feed, so both slots are
    // strictly required.
    files: [
      {
        key: "wasteTracking",
        label: "Waste Tracking Data CSV (monthly)",
        accept: ".csv",
        required: true,
      },
      { key: "biennial", label: "Biennial Data CSV (monthly)", accept: ".csv", required: true },
    ],
    fixedTenant: { id: TENANT_IDS.PETRO_STAR, name: "Petro Star" },
  },
  "ups-veolia": {
    label: "UPS Veolia",
    // Spec §1.1 lists two files per production run (one haz, one non-haz),
    // but both have the same `WasteGenMgtFlat` schema and downstream
    // transforms are identical. Neither slot is strictly required on its
    // own — `requireAtLeastOneOf` enforces that the run must still supply
    // at least one of them. UI and resolver both honor this rule.
    files: [
      {
        key: "hazardous",
        label: "Haz Waste Management Report",
        accept: ".xlsx,.xls",
        required: false,
      },
      {
        key: "nonHazardous",
        label: "Non-Haz Waste Management Report",
        accept: ".xlsx,.xls",
        required: false,
      },
    ],
    requireAtLeastOneOf: ["hazardous", "nonHazardous"],
    fixedTenant: { id: TENANT_IDS.UPS, name: "UPS" },
  },
  "ups-world-oil": {
    label: "UPS World Oil",
    files: [
      {
        key: "source",
        label: "World Oil Activity Report (.xlsx)",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.UPS, name: "UPS" },
  },
} as const satisfies Record<string, WastePipelineConfig>;

export type WastePipelineName = keyof typeof WASTE_PIPELINE_REGISTRY;

export function isWastePipelineName(name: string): name is WastePipelineName {
  return name in WASTE_PIPELINE_REGISTRY;
}

export const WASTE_PIPELINE_NAMES =
  Object.keys(WASTE_PIPELINE_REGISTRY).filter(isWastePipelineName);

/**
 * Tenant-specific variants of vendor-generic pipelines. Dispatching the
 * vendor-generic pipeline for one of these tenants routes to the variant
 * instead: it parses the same vendor format but adds tenant addendum rules
 * (extra tags, enrichments) that the generic run would silently skip. The
 * dispatch UI switches its selection when the tenant is picked; the dispatch
 * resolver remaps as a backstop for direct API callers.
 *
 * Invariants (enforced by wastePipelineRegistry.test.ts): the base pipeline
 * is vendor-generic (`fixedTenant: null`), the variant's `fixedTenant.id`
 * matches its tenant key, and the variant's file slot keys are a subset of
 * the base's so files selected before the tenant carry over.
 */
export const WASTE_PIPELINE_TENANT_VARIANTS: Partial<
  Record<WastePipelineName, Readonly<Record<string, WastePipelineName>>>
> = {
  "clean-harbors": {
    [TENANT_IDS.PETRO_STAR]: "petro-star-clean-harbors",
  },
};

export function getWastePipelineTenantVariant(
  name: WastePipelineName,
  tenantId: string
): WastePipelineName | undefined {
  return WASTE_PIPELINE_TENANT_VARIANTS[name]?.[tenantId];
}

/**
 * Widening accessor for the registry. `WASTE_PIPELINE_REGISTRY` is declared
 * `as const satisfies Record<...>`, which preserves literal types for fields
 * like `fixedTenant` but drops optional fields that aren't set on every
 * pipeline (e.g. `requireAtLeastOneOf`). Reading through this helper gives
 * the consumer the full `WastePipelineConfig` shape without an `as` cast.
 */
export function getWastePipelineConfig(name: WastePipelineName): WastePipelineConfig {
  return WASTE_PIPELINE_REGISTRY[name];
}
